/**
 * Core DeleteButton Component
 *
 * RBAC-gated delete button with built-in confirmation dialog.
 * Handles the full delete flow: click → confirm → async action → toast feedback.
 *
 * @example
 *   <DeleteButton
 *     permission={PERMISSIONS.DATASOURCES_DELETE}
 *     itemName="PostgreSQL Production"
 *     onDelete={async () => await api.delete(id)}
 *   />
 */
import React, { useState } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '../primitives/Button';
import { ConfirmDialog } from '../feedback/ConfirmDialog';
import { notify } from '../feedback/Toast';
import { useAuth } from '@/context/useAuth';
import { hasPermission } from '@/utils/permissions';

export interface DeleteButtonProps {
  /** Async delete handler */
  onDelete: () => Promise<void> | void;
  /** Name of the item being deleted (shown in confirm dialog) */
  itemName?: string;
  /** RBAC permission required */
  permission?: string;
  /** Custom confirm title */
  confirmTitle?: string;
  /** Custom confirm description */
  confirmDescription?: string;
  /** Button label */
  label?: string;
  /** Show as icon-only */
  iconOnly?: boolean;
  /** Size */
  size?: 'xs' | 'sm' | 'md';
  /** Disabled */
  disabled?: boolean;
  /** Skip confirmation dialog */
  skipConfirm?: boolean;
  /** Toast messages */
  successMessage?: string;
  errorMessage?: string;
  className?: string;
}

export const DeleteButton: React.FC<DeleteButtonProps> = ({
  onDelete,
  itemName = 'this item',
  permission,
  confirmTitle,
  confirmDescription,
  label = 'Delete',
  iconOnly = false,
  size = 'sm',
  disabled = false,
  skipConfirm = false,
  successMessage,
  errorMessage,
  className = '',
}) => {
  const { user } = useAuth();
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  if (permission && !hasPermission(user, permission)) return null;

  const handleDelete = async () => {
    setLoading(true);
    try {
      await onDelete();
      notify.success(successMessage || `${itemName} deleted successfully`);
      setShowConfirm(false);
    } catch (err: any) {
      notify.error(errorMessage || `Failed to delete ${itemName}`, {
        description: err?.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (skipConfirm) {
      handleDelete();
    } else {
      setShowConfirm(true);
    }
  };

  return (
    <>
      <Button
        variant="danger"
        size={size}
        leftIcon={<Trash2 className={size === 'xs' ? 'h-3 w-3' : 'h-4 w-4'} />}
        onClick={handleClick}
        disabled={disabled || loading}
        className={className}
      >
        {iconOnly ? null : label}
      </Button>

      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleDelete}
        variant="danger"
        title={confirmTitle || `Delete ${itemName}?`}
        description={confirmDescription || `This will permanently delete "${itemName}". This action cannot be undone.`}
        confirmText="Delete"
        loading={loading}
      />
    </>
  );
};
export default DeleteButton;
